// 权限码目录与「人话摘要」（文档 20 §4：按资源分组 + 人话摘要，给业务负责人审核用）。
// 权限码、中文名、限制项的拒绝映射逐字照老系统 REPO/src/lib/role-permission-catalog.ts；
// 数据范围照 REPO/src/lib/customers.ts scopeCustomerWhereForUser、order-visibility.ts。
// 这里只做「展示」：真正的鉴权永远在服务端。
import type { Role } from './types';

/** 老系统的 94 个权限码 → 中文名（role-permission-catalog.ts rolePermissionOptionGroups） */
export const PERM_LABEL: Record<string, string> = {
  'dashboard.read': '查看工作台',
  'reports.business.read': '查看经营报表',
  'reports.business.export': '导出经营数据',
  'orders.read.all': '查看全部订单',
  'orders.read.own': '查看自己创建的订单',
  'orders.read.assigned': '查看分配给自己的订单',
  'orders.create': '新建业务订单',
  'orders.edit.all': '修改未发货的全部订单',
  'orders.edit.own_unshipped': '修改未发货的自己订单',
  'orders.change.request': '申请修改已审核的自己订单',
  'orders.change.approve': '审核订单变更',
  'orders.change.acknowledge': '确认已知晓订单变更',
  'orders.submit': '提交库存预占',
  'orders.approve': '审核订单',
  'orders.assign': '分配跟单',
  'orders.cancel': '取消订单',
  'orders.close': '终止剩余发货并结案',
  'orders.delete': '删除未履约订单',
  'orders.amount.read': '查看订单金额',
  'orders.invoice.manage': '维护订单开票信息',
  'taobao.manage': '管理淘宝店',
  'shipments.read': '查看发货批次',
  'shipments.ship': '处理发货出库',
  'shipments.console': '使用仓库操作台',
  'shipments.quantity.write': '录入本次发货数量',
  'shipments.damage.write': '录入破损/补耗数量',
  'shipments.logistics.write': '录入物流公司和物流单号',
  'exceptions.read': '查看异常售后',
  'exceptions.create': '新增异常售后',
  'exceptions.resolve': '处理异常售后',
  'exceptions.close': '关闭异常售后',
  'customers.read': '查看客户资料',
  'customers.create': '新建客户资料',
  'customers.edit': '编辑客户资料',
  'customers.delete': '删除客户资料',
  'customers.invoice.write': '维护客户默认开票资料',
  'customers.settlement.write': '维护客户月结设置',
  'products.read': '查看产品资料',
  'products.write': '维护产品资料',
  'gallery.read': '查看产品画册',
  'products.category.write': '维护产品分类',
  'products.bundle.write': '维护产品套装',
  'products.packaging.quote.write': '维护产品包装方案报价',
  'inventory.read': '查看库存总览',
  'inventory.transactions.read': '查看库存流水',
  'inventory.in.write': '手工入库',
  'inventory.out.write': '手工出库',
  'inventory.adjust.write': '库存调整/报损',
  'inventory.import.initial_stock': '导入初始库存',
  'imports.read': '查看导入导出历史',
  'imports.errors.download': '下载失败行',
  'warehouses.write': '维护仓库',
  'purchase.read': '查看采购订单',
  'purchase.create': '新建采购订单',
  'purchase.edit': '维护采购订单',
  'purchase.delete': '删除未入库采购单',
  'purchase.receive': '采购转入库',
  'purchase.pdf.write': '生成采购单 PDF',
  'purchase.suggestions.read': '查看采购建议',
  'suppliers.read': '查看供应商资料',
  'suppliers.write': '维护供应商资料',
  'suppliers.settlement.write': '维护供应商月结设置',
  'settlements.overview.read': '查看结算总览',
  'settlements.customer.read': '查看客户结算',
  'settlements.customer.cash.write': '登记客户收款',
  'settlements.customer.invoice.write': '登记客户开票',
  'settlements.supplier.read': '查看供应商结算',
  'settlements.supplier.cash.write': '登记供应商付款',
  'settlements.supplier.invoice.write': '登记供应商收票',
  'settlements.statement.read': '查看对账单',
  'settlements.statement.write': '生成/保存对账单',
  'settlements.statement.confirm': '确认/反确认对账单',
  'settlements.statement.lock': '锁账/解锁对账单',
  'settlements.statement.pdf.write': '生成对账单 PDF',
  'settlements.allocation.write': '执行收付款/发票核销',
  'users.read': '查看账号列表',
  'users.create': '新增账号',
  'users.edit': '编辑账号资料',
  'users.status.write': '启停账号',
  'users.password.reset': '重置账号密码',
  'users.delete': '删除账号',
  'roles.read': '查看角色列表',
  'roles.create': '新增角色',
  'roles.edit': '编辑角色',
  'roles.delete': '删除角色',
  'roles.preset.manage': '管理预置角色权限',
  'system.settings.read': '查看系统设置',
  'system.settings.write': '修改系统设置',
  'system.security.write': '修改账号安全策略',
  'system.logistics.write': '管理物流公司',
  'system.shops.write': '管理店铺名称',
  'audit.read': '查看操作日志',
  'audit.delete': '删除操作日志',
  'uploads.write': '上传文件',
};
export const ALL_PERMS = Object.keys(PERM_LABEL);

/** 摘要里用的短动词（「能 开单、提交预占…」） */
const VERB: Record<string, string> = {
  'dashboard.read': '进主屏', 'reports.business.read': '看经营报表', 'reports.business.export': '导出经营数据',
  'orders.create': '开单', 'orders.edit.all': '改全部未发货订单', 'orders.edit.own_unshipped': '改自己未发货的订单',
  'orders.change.request': '申请修改已审核订单', 'orders.change.approve': '审核变更单', 'orders.change.acknowledge': '确认知晓变更',
  'orders.submit': '提交预占', 'orders.approve': '审核订单', 'orders.assign': '分配跟单', 'orders.cancel': '取消订单',
  'orders.close': '结案', 'orders.delete': '删除订单', 'orders.invoice.manage': '维护开票',
  'taobao.manage': '淘宝店开单、出库、退货',
  'shipments.read': '看发货批次', 'shipments.ship': '发货出库', 'shipments.console': '用仓库操作台', 'shipments.quantity.write': '录发货数量',
  'shipments.damage.write': '录破损/补耗', 'shipments.logistics.write': '录物流单号',
  'exceptions.read': '看异常', 'exceptions.create': '登记异常', 'exceptions.resolve': '处理异常', 'exceptions.close': '关闭异常',
  'customers.read': '看客户', 'customers.create': '新建客户', 'customers.edit': '改客户资料', 'customers.delete': '删除客户',
  'customers.invoice.write': '维护开票资料', 'customers.settlement.write': '维护月结设置',
  'products.read': '看产品', 'products.write': '维护产品', 'gallery.read': '看画册', 'products.category.write': '维护分类',
  'products.bundle.write': '维护套装', 'products.packaging.quote.write': '维护包装报价',
  'inventory.read': '看库存', 'inventory.transactions.read': '看流水', 'inventory.in.write': '手工入库', 'inventory.out.write': '手工出库',
  'inventory.adjust.write': '调整/报损', 'inventory.import.initial_stock': '导入期初', 'imports.read': '看导入历史',
  'imports.errors.download': '下载失败行', 'warehouses.write': '维护仓库',
  'purchase.read': '看采购单', 'purchase.create': '下采购单', 'purchase.edit': '改采购单', 'purchase.delete': '删采购单',
  'purchase.receive': '采购转入库', 'purchase.pdf.write': '出采购单 PDF', 'purchase.suggestions.read': '看采购建议',
  'suppliers.read': '看供应商', 'suppliers.write': '维护供应商', 'suppliers.settlement.write': '维护供应商月结',
  'settlements.overview.read': '看结算总览', 'settlements.customer.read': '看客户结算', 'settlements.customer.cash.write': '登记收款',
  'settlements.customer.invoice.write': '登记开票', 'settlements.supplier.read': '看供应商结算', 'settlements.supplier.cash.write': '登记付款',
  'settlements.supplier.invoice.write': '登记收票', 'settlements.statement.read': '看对账单', 'settlements.statement.write': '出对账单',
  'settlements.statement.confirm': '确认对账单', 'settlements.statement.lock': '锁账', 'settlements.statement.pdf.write': '导出对账单',
  'settlements.allocation.write': '执行核销',
  'users.read': '看账号', 'users.create': '新增账号', 'users.edit': '改账号', 'users.status.write': '启停账号',
  'users.password.reset': '重置密码', 'users.delete': '删除账号', 'roles.read': '看角色', 'roles.create': '新建角色',
  'roles.edit': '改角色', 'roles.delete': '删除角色', 'roles.preset.manage': '改预置角色', 'system.settings.read': '看系统设置',
  'system.settings.write': '改系统设置', 'system.security.write': '改安全策略', 'system.logistics.write': '管物流公司',
  'system.shops.write': '管店铺', 'audit.read': '看操作日志', 'audit.delete': '清理日志', 'uploads.write': '上传文件',
};
export const verb = (code: string) => VERB[code] || PERM_LABEL[code] || code;

/* ------------------------------ 限制项（显式拒绝，永远压倒授权） ------------------------------ */

const WRITE_LIKE = (p: string) =>
  p.endsWith('.write') || p.endsWith('.create') || p.endsWith('.edit') || p.endsWith('.delete') ||
  ['orders.submit', 'orders.approve', 'orders.assign', 'orders.cancel', 'orders.close', 'purchase.receive', 'settlements.statement.confirm', 'settlements.statement.lock'].includes(p);

/** 老系统 roleRestrictionDeniedPermissions（24 条映射型限制项） */
export const RESTRICTION_DENIES: Record<string, string[]> = {
  '不能查看产品成本与入库均价': ['purchase.pdf.write'],
  '不能查看供应商报价': ['products.packaging.quote.write'],
  '不能查看账号列表': ['users.read'],
  '不能新增或编辑账号': ['users.create', 'users.edit'],
  '不能启停账号或重置密码': ['users.status.write', 'users.password.reset'],
  '不能查看角色列表': ['roles.read'],
  '不能创建、编辑、删除角色': ['roles.create', 'roles.edit', 'roles.delete'],
  '不能管理预置角色权限': ['roles.preset.manage'],
  '不能查看操作日志': ['audit.read'],
  '不能删除操作日志': ['audit.delete'],
  '不能查看系统设置': ['system.settings.read'],
  '不能修改系统设置': ['system.settings.write', 'system.security.write'],
  '不能维护物流公司': ['system.logistics.write'],
  '不能维护店铺名称': ['system.shops.write'],
  '不能上传文件': ['uploads.write'],
  '不能审核订单': ['orders.approve'],
  '不能审核分配跟单': ['orders.approve', 'orders.assign'],
  '不能分配跟单': ['orders.assign'],
  '不能审核订单和处理客户发货': ['orders.approve', 'orders.assign', 'shipments.ship', 'shipments.console', 'shipments.quantity.write', 'shipments.damage.write', 'shipments.logistics.write'],
  '不能查看未分配给自己的订单': ['orders.read.all', 'orders.read.own'],
  '不能查看订单金额、产品单价和明细金额': ['orders.amount.read'],
  '不能执行入库、发货或审核操作': ['orders.approve', 'orders.assign', 'shipments.ship', 'shipments.console', 'shipments.quantity.write', 'shipments.damage.write', 'shipments.logistics.write', 'inventory.in.write', 'inventory.out.write', 'inventory.adjust.write', 'purchase.receive'],
  '不能查看往来结算': ['settlements.overview.read', 'settlements.customer.read', 'settlements.supplier.read', 'settlements.statement.read'],
  '不能登记收付款或发票': ['settlements.customer.cash.write', 'settlements.customer.invoice.write', 'settlements.supplier.cash.write', 'settlements.supplier.invoice.write', 'settlements.allocation.write'],
  '不能导出对账单': ['settlements.statement.pdf.write', 'reports.business.export'],
  '不能新增、修改、删除任何业务资料': ALL_PERMS.filter(WRITE_LIKE),
};
/** 唯一不走映射表的限制项：字段脱敏（order-visibility.ts:92） */
export const HIDE_SALES_USER = '不能查看具体业务员';

/** 实际生效的权限 = 授予 − 限制项拒绝；角色停用则全部失效（role-permissions.ts resolveUserAccessControl） */
export function effectivePerms(role: Pick<Role, 'permissions' | 'restrictions' | 'isActive' | 'warehouseIds'>): Set<string> {
  if (!role.isActive) return new Set();
  const denied = new Set(role.restrictions.flatMap((r) => RESTRICTION_DENIES[r] || []));
  return new Set(role.permissions.filter((p) => !denied.has(p) && (!role.warehouseIds || (!p.startsWith('settlements.') && !p.startsWith('audit.') && !p.startsWith('imports.')))));
}

/** 被限制项拒绝掉的授权（授予了、但被限制项压掉） */
export function overriddenPerms(role: Pick<Role, 'permissions' | 'restrictions'>): string[] {
  const denied = new Set(role.restrictions.flatMap((r) => RESTRICTION_DENIES[r] || []));
  return role.permissions.filter((p) => denied.has(p));
}

/* ------------------------------ 资源分组 ------------------------------ */

export type GenericAction = 'view' | 'create' | 'edit' | 'delete' | 'export';
export const GENERIC_LABEL: Record<GenericAction, string> = { view: '查看', create: '新建', edit: '编辑', delete: '删除', export: '导出' };

export type PermGroup = {
  key: string;
  name: string;
  sub: string;
  /** 通用动作 → 满足任一码即算有；没有这个动作的资源不写（界面画「—」） */
  generic?: Partial<Record<GenericAction, string[]>>;
  /** 专项操作（通用五列装不下的） */
  special: string[];
};

/** 11 个资源分组（文档 20 §4.2 ①：行是资源，列是通用动作 + 专项）。订单金额归「敏感字段」，不在这里 */
export const GROUPS: PermGroup[] = [
  {
    key: 'orders', name: '订单', sub: '开单、审核、变更',
    generic: { view: ['orders.read.all', 'orders.read.own', 'orders.read.assigned'], create: ['orders.create'], edit: ['orders.edit.all', 'orders.edit.own_unshipped'], delete: ['orders.delete'] },
    special: ['orders.submit', 'orders.approve', 'orders.assign', 'orders.change.request', 'orders.change.approve', 'orders.change.acknowledge', 'orders.cancel', 'orders.close', 'orders.invoice.manage'],
  },
  {
    key: 'shipments', name: '发货', sub: '批次、出库、物流',
    generic: { view: ['shipments.read'], create: ['shipments.ship'] },
    special: ['shipments.console', 'shipments.quantity.write', 'shipments.damage.write', 'shipments.logistics.write'],
  },
  {
    key: 'exceptions', name: '异常售后', sub: '少发、破损、补发、退货',
    generic: { view: ['exceptions.read'], create: ['exceptions.create'] },
    special: ['exceptions.resolve', 'exceptions.close'],
  },
  {
    key: 'customers', name: '客户', sub: '资料、开票、月结',
    generic: { view: ['customers.read'], create: ['customers.create'], edit: ['customers.edit'], delete: ['customers.delete'] },
    special: ['customers.invoice.write', 'customers.settlement.write'],
  },
  {
    key: 'products', name: '产品', sub: '档案、画册、套装',
    generic: { view: ['products.read'], create: ['products.write'], edit: ['products.write'], delete: ['products.write'] },
    special: ['gallery.read', 'products.category.write', 'products.bundle.write', 'products.packaging.quote.write'],
  },
  {
    key: 'inventory', name: '库存', sub: '总览、流水、出入库',
    generic: { view: ['inventory.read'] },
    special: ['inventory.transactions.read', 'inventory.in.write', 'inventory.out.write', 'inventory.adjust.write', 'inventory.import.initial_stock', 'imports.read', 'imports.errors.download', 'warehouses.write'],
  },
  {
    key: 'purchase', name: '采购', sub: '采购单、建议、供应商',
    generic: { view: ['purchase.read'], create: ['purchase.create'], edit: ['purchase.edit'], delete: ['purchase.delete'], export: ['purchase.pdf.write'] },
    special: ['purchase.receive', 'purchase.suggestions.read', 'suppliers.read', 'suppliers.write', 'suppliers.settlement.write'],
  },
  {
    key: 'settlements', name: '往来结算', sub: '台账、核销、对账单',
    generic: { view: ['settlements.overview.read'], export: ['settlements.statement.pdf.write'] },
    special: ['settlements.customer.read', 'settlements.customer.cash.write', 'settlements.customer.invoice.write', 'settlements.supplier.read', 'settlements.supplier.cash.write', 'settlements.supplier.invoice.write', 'settlements.statement.read', 'settlements.statement.write', 'settlements.statement.confirm', 'settlements.statement.lock', 'settlements.allocation.write'],
  },
  {
    key: 'taobao', name: '淘宝店', sub: '独立业务线',
    generic: { view: ['taobao.manage'], create: ['taobao.manage'], edit: ['taobao.manage'] },
    special: [],
  },
  {
    key: 'reports', name: '主屏与报表', sub: '待办、经营报表',
    generic: { view: ['reports.business.read'], export: ['reports.business.export'] },
    special: ['dashboard.read'],
  },
  {
    key: 'system', name: '系统', sub: '账号、角色、设置、日志',
    special: ['users.read', 'users.create', 'users.edit', 'users.status.write', 'users.password.reset', 'users.delete', 'roles.read', 'roles.create', 'roles.edit', 'roles.delete', 'roles.preset.manage', 'system.settings.read', 'system.settings.write', 'system.security.write', 'system.logistics.write', 'system.shops.write', 'audit.read', 'audit.delete', 'uploads.write'],
  },
];

export function groupCodes(g: PermGroup): string[] {
  const set = new Set<string>();
  Object.values(g.generic || {}).forEach((cs) => cs?.forEach((c) => set.add(c)));
  g.special.forEach((c) => set.add(c));
  return [...set];
}

export type GroupSummary = { on: number; total: number; can: string[]; cannot: string[]; none: boolean; readOnly: boolean };

/** 每组一句人话：能做什么 + （红字）不能做的关键动作 */
export function summarizeGroup(g: PermGroup, perms: Set<string>): GroupSummary {
  const codes = groupCodes(g);
  const on = codes.filter((c) => perms.has(c));
  const viewCodes = g.generic?.view || [];
  const writes = on.filter((c) => !viewCodes.includes(c) && !c.endsWith('.read'));
  const can = unique(on.filter((c) => !viewCodes.includes(c)).map(verb));
  const cannot = unique(codes.filter((c) => !perms.has(c) && KEY_DENY.has(c)).map(verb));
  if (g.key === 'taobao') return perms.has('taobao.manage') ? { on: 1, total: 1, can: [verb('taobao.manage')], cannot: [], none: false, readOnly: false } : { on: 0, total: 1, can: [], cannot: [], none: true, readOnly: false };
  // 「查看」的几个码（订单的全部 / 本人 / 分配给我）只算一项：范围归「数据范围」管，不在这里重复计数
  const others = codes.filter((c) => !viewCodes.includes(c));
  const onCount = (viewCodes.some((c) => perms.has(c)) ? 1 : 0) + others.filter((c) => perms.has(c)).length;
  const total = (viewCodes.length ? 1 : 0) + others.length;
  return { on: onCount, total, can, cannot, none: on.length === 0, readOnly: on.length > 0 && writes.length === 0 };
}

/** 摘要里值得点名「不能」的动作（不列鸡毛蒜皮） */
const KEY_DENY = new Set([
  'orders.create', 'orders.approve', 'orders.assign', 'orders.delete', 'orders.cancel', 'orders.close',
  'shipments.ship', 'shipments.damage.write',
  'exceptions.create', 'exceptions.close',
  'customers.edit', 'customers.delete',
  'products.write', 'products.bundle.write',
  'inventory.in.write', 'inventory.out.write', 'inventory.adjust.write',
  'purchase.create', 'purchase.receive',
  'settlements.customer.cash.write', 'settlements.allocation.write',
  'users.create', 'roles.edit', 'system.settings.write', 'audit.read',
]);

function unique(xs: string[]) {
  return [...new Set(xs)];
}

/* ------------------------------ 数据范围 / 敏感字段 ------------------------------ */

export type Scope = { key: string; label: string; options: { label: string; on: boolean }[]; note?: string };

export function dataScopes(role: Pick<Role, 'code' | 'baseRole' | 'warehouseIds'>, perms: Set<string>): Scope[] {
  const all = perms.has('orders.read.all');
  const own = perms.has('orders.read.own');
  const assigned = perms.has('orders.read.assigned');
  const cust = customerScope(role);
  const settleVisible = ['settlements.overview.read', 'settlements.customer.read', 'settlements.supplier.read', 'settlements.statement.read'].some((p) => perms.has(p));
  const scopes: Scope[] = [
    {
      key: 'orders', label: '订单能看哪些',
      options: [{ label: '全部订单', on: all }, { label: '本人创建的', on: !all && own }, { label: '分配给我的', on: !all && assigned }, { label: '不可见', on: !all && !own && !assigned }],
      note: !all && own && assigned ? '本人创建的和分配给我的都能看（两项叠加）' : undefined,
    },
    {
      key: 'customers', label: '客户能看哪些',
      options: [{ label: '全部客户', on: cust === 'all' }, { label: '本人名下的', on: cust === 'owned' }, { label: '有分配给我订单的', on: cust === 'assigned' }],
      note: '老系统按角色模板决定，不走权限码（customers.ts）',
    },
    {
      key: 'warehouses', label: '仓库能看哪些',
      options: [{ label: '全部仓库', on: role.warehouseIds === null }, { label: `指定仓库（${role.warehouseIds?.length || 0} 个）`, on: role.warehouseIds !== null }],
      note: role.warehouseIds?.length ? `仓库 ID：${role.warehouseIds.join('、')}` : undefined,
    },
    {
      key: 'settlements', label: '往来结算',
      options: [
        { label: '客户结算', on: perms.has('settlements.customer.read') },
        { label: '供应商结算', on: perms.has('settlements.supplier.read') },
        { label: '对账单', on: perms.has('settlements.statement.read') },
        { label: '不可见', on: !settleVisible },
      ],
      note: settleVisible ? `客户侧范围与客户范围一致（${CUST_LABEL[cust]}）${role.code === 'TAOBAO_MANAGER' ? '；供应商侧只看淘宝店相关' : ''}` : undefined,
    },
  ];
  return scopes;
}

const CUST_LABEL = { all: '全部客户', owned: '本人名下的客户', assigned: '有分配给我订单的客户' } as const;

/** customers.ts scopeCustomerWhereForUser：淘宝店管理 / 业务员模板 → 本人名下；跟单模板 → 有分配给我订单的；其余全部 */
export function customerScope(role: Pick<Role, 'code' | 'baseRole'>): keyof typeof CUST_LABEL {
  if (role.code === 'TAOBAO_MANAGER' || role.baseRole === 'SALES') return 'owned';
  if (role.baseRole === 'FOLLOWER') return 'assigned';
  return 'all';
}

/** 列表上的数据范围色点：全部 = 绿，受限 = 琥珀，看不到订单 = 灰 */
export function scopeBrief(role: Pick<Role, 'code' | 'baseRole'>, perms: Set<string>): { label: string; tone: 'green' | 'amber' | 'grey' } {
  if (perms.has('orders.read.all')) return { label: '全部订单', tone: 'green' };
  const own = perms.has('orders.read.own');
  const assigned = perms.has('orders.read.assigned');
  if (own && assigned) return { label: '本人 + 分配给我', tone: 'amber' };
  if (own) return { label: '本人创建', tone: 'amber' };
  if (assigned) return { label: '分配给我', tone: 'amber' };
  if (customerScope(role) === 'owned' && perms.has('taobao.manage')) return { label: '本人名下客户', tone: 'amber' };
  return { label: '不看订单', tone: 'grey' };
}

export type Sensitive = { label: string; desc: string; visible: boolean; source: string };

/** 敏感字段：共享服务端接口按角色限制项裁剪。 */
export function sensitiveFields(role: Pick<Role, 'restrictions'>, perms: Set<string>): Sensitive[] {
  return [
    { label: '订单金额', desc: '订单金额汇总', visible: perms.has('orders.amount.read'), source: 'orders.amount.read' },
    { label: '产品单价和明细金额', desc: '订单明细单价与金额', visible: perms.has('orders.amount.read') && !role.restrictions.includes('不能查看产品单价和明细金额'), source: '不能查看产品单价和明细金额' },
    { label: '业务员姓名', desc: '订单列表与详情的业务员', visible: !role.restrictions.includes(HIDE_SALES_USER), source: `限制项「${HIDE_SALES_USER}」` },
    { label: '客户联系方式', desc: '客户电话、地址及订单收货联系方式', visible: !role.restrictions.includes('不能查看客户联系方式'), source: '不能查看客户联系方式' },
    { label: '产品成本价与入库均价', desc: '产品及库存成本字段', visible: !role.restrictions.includes('不能查看产品成本与入库均价'), source: '不能查看产品成本与入库均价' },
    { label: '供应商报价', desc: '包装方案关联供应商报价', visible: !role.restrictions.includes('不能查看供应商报价'), source: '不能查看供应商报价' },
  ];
}

/* ------------------------------ 整个角色的人话摘要 ------------------------------ */

const HEADLINE_ORDER = [
  'orders.create', 'orders.submit', 'orders.approve', 'orders.assign', 'orders.change.request', 'shipments.ship', 'shipments.quantity.write',
  'exceptions.resolve', 'inventory.in.write', 'inventory.out.write', 'inventory.adjust.write', 'purchase.create', 'purchase.receive',
  'suppliers.write', 'settlements.customer.cash.write', 'settlements.allocation.write', 'settlements.statement.write', 'taobao.manage',
  'customers.edit', 'products.write', 'users.create', 'roles.edit', 'system.settings.write',
];
const HEADLINE_VERB: Record<string, string> = {
  'orders.create': '开单', 'orders.submit': '提交预占', 'orders.approve': '审核订单', 'orders.assign': '分配跟单',
  'orders.change.request': '申请修改已审核订单', 'shipments.ship': '发货出库', 'shipments.quantity.write': '录入发货',
  'exceptions.resolve': '处理异常', 'inventory.in.write': '入库', 'inventory.out.write': '出库', 'inventory.adjust.write': '盘点调整',
  'purchase.create': '下采购单', 'purchase.receive': '采购转入库', 'suppliers.write': '维护供应商',
  'settlements.customer.cash.write': '登记收付款', 'settlements.allocation.write': '执行核销', 'settlements.statement.write': '出对账单',
  'taobao.manage': '淘宝店开单出库', 'customers.edit': '维护客户', 'products.write': '维护产品', 'users.create': '管理账号',
  'roles.edit': '管理角色', 'system.settings.write': '修改系统设置',
};

export type RoleSummary = { headline: string; hidden: string[]; can: string[]; cannot: string[] };

/**
 * 「能干什么」：由当前生效权限自动生成（不是手写的）。
 * headline 一句总述（卡片、表格用）；can / cannot 是绿块 / 红块的逐条（详情用）。
 */
export function summarizeRole(role: Role): RoleSummary {
  const perms = effectivePerms(role);
  const sens = sensitiveFields(role, perms);
  const hidden = sens.filter((x) => !x.visible).map((x) => (x.label.startsWith('订单金额') ? '订单金额' : x.label));
  if (!role.isActive) return { headline: '角色已停用，使用它的账号没有任何权限。', hidden: [], can: [], cannot: ['角色已停用：全部权限失效'] };

  const writes = HEADLINE_ORDER.filter((c) => perms.has(c));
  const allPerms = ALL_PERMS.filter((c) => c !== 'orders.read.own' && c !== 'orders.read.assigned').every((c) => perms.has(c));
  let headline: string;
  if (allPerms) headline = '全部功能。审核订单、分配跟单、删除订单、管理账号与角色、修改安全策略。';
  else if (!writes.length) {
    const seen = GROUPS.filter((g) => g.key !== 'system' && summarizeGroup(g, perms).on > 0).map((g) => g.name);
    headline = seen.length ? `只读。可以看${seen.slice(0, 5).join('、')}${seen.length > 5 ? '等' : ''}，不能做任何写操作。` : '几乎没有权限。';
  } else headline = `${writes.slice(0, 5).map((c) => HEADLINE_VERB[c]).join('、')}${writes.length > 5 ? ' 等' : ''}。`;

  const brief = scopeBrief(role, perms);
  const can: string[] = [];
  const cannot: string[] = [];
  if (brief.tone !== 'grey') can.push(`看${brief.label === '全部订单' ? '全部' : `「${brief.label}」的`}订单`);
  for (const g of GROUPS) {
    const sm = summarizeGroup(g, perms);
    if (sm.none) {
      if (g.key === 'system') cannot.push('不能管账号、角色和系统设置，看不到操作日志');
      else if (g.key === 'reports') cannot.push('看不到主屏和经营报表');
      else if (g.key !== 'taobao') cannot.push(`看不到${g.name}`);
      continue;
    }
    if (sm.can.length) can.push(`${g.name}：${sm.can.slice(0, 6).join('、')}${sm.can.length > 6 ? ` 等 ${sm.can.length} 项` : ''}`);
    else can.push(`${g.name}：只能看`);
    if (sm.cannot.length) cannot.push(`${g.name}：不能${sm.cannot.slice(0, 4).join('、')}`);
  }
  if (hidden.length) cannot.unshift(`看不到${hidden.join('和')}`);
  return { headline, hidden, can, cannot };
}
